import React from "react";
import { Badge } from "@/components/ui/badge";
import { Check, X } from "lucide-react";
import type { Performer } from "@shared/schema";
import type { ParticipantMode } from "./mode-config";

interface ParticipantBadgeProps {
  performer: Performer;
  mode: ParticipantMode;
  isDragging?: boolean;
}

export function ParticipantBadge({ performer, mode, isDragging }: ParticipantBadgeProps) {
  const isGenericEvent = mode === 'generic-event';
  const isOpenMic = mode === 'open-mic';

  const badgeStyle = isDragging ? {} : {
    boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  };

  const badgeClassName = isDragging 
    ? "bg-blue-100 text-blue-800 border border-blue-200 shadow-sm" 
    : "bg-blue-100/60 backdrop-blur-sm text-blue-800/90 border border-blue-200/50";

  if (isGenericEvent) {
    return performer.attended ? (
      <div className="flex items-center text-green-600/90">

      </div>
    ) : (
      <div className="flex items-center text-slate-400/80">

      </div>
    );
  }

  return (
    <Badge
      className={
        performer.played
          ? "bg-green-100/40 backdrop-blur-sm text-green-800 border-green-200/50 shadow-sm"
          : "bg-slate-100/40 backdrop-blur-sm text-slate-600 border-slate-200/50 shadow-sm"
      }
    >
      {performer.played ? (
        <>
          <Check className="h-3 w-3 mr-1" />
          Played{(isOpenMic && performer.performanceCount > 0) ? ` (${performer.performanceCount})` : ''}
        </>
      ) : (
        <>
          <X className="h-3 w-3 mr-1" />
          Not Played
        </>
      )}
    </Badge>
  );
}

interface InstrumentBadgesProps {
  instruments?: string[];
  isDragging?: boolean;
}

export function InstrumentBadges({ instruments, isDragging }: InstrumentBadgesProps) {
  if (!instruments?.length) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {instruments.map((instrument) => (
        <Badge
          key={instrument}
          variant="secondary"
          className="bg-blue-100/40 backdrop-blur-sm text-blue-800 border-blue-200/50 shadow-sm"
        >
          {instrument}
        </Badge>
      ))}
    </div>
  );
}

interface TypeBadgeProps {
  performerType?: string | null;
  isDragging?: boolean;
}

export function TypeBadge({ performerType, isDragging }: TypeBadgeProps) {
  const badgeStyle = isDragging ? {} : {
    boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  };

  const badgeClassName = isDragging 
    ? "bg-blue-100 text-blue-800 border border-blue-200 shadow-sm" 
    : "bg-blue-100/60 backdrop-blur-sm text-blue-800/90 border border-blue-200/50";

  return (
    <Badge 
      variant="outline" 
      className={`capitalize ${badgeClassName}`} 
      style={badgeStyle}
    >
      {performerType || 'solo'}
    </Badge>
  );
}

interface TierBadgeProps {
  tier?: string | null;
}

export function TierBadge({ tier }: TierBadgeProps) {
  if (!tier) {
    return null;
  }

  return (
    <Badge
      variant="outline"
      className="bg-purple-50/40 backdrop-blur-sm text-purple-700 border-purple-200/50 shadow-sm"
    >
      {tier}
    </Badge>
  );
}

interface PriorityBadgeProps {
  priority?: boolean;
  isDragging?: boolean;
}

export function PriorityBadge({ priority, isDragging }: PriorityBadgeProps) {
  if (!priority) {
    return <span className="text-slate-400/80 text-sm drop-shadow-sm">-</span>;
  }

  const badgeStyle = isDragging ? {} : {
    boxShadow: '0 2px 8px rgba(251,146,60,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  };

  const badgeClassName = isDragging 
    ? "bg-orange-100 text-orange-800 border border-orange-200 shadow-sm" 
    : "bg-orange-100/60 backdrop-blur-sm text-orange-800/90 border border-orange-200/50";

  return (
    <Badge className={badgeClassName} style={badgeStyle}>
      <div className="w-2 h-2 bg-orange-500 rounded-full mr-1 animate-pulse drop-shadow-sm"></div>
      Priority
    </Badge>
  );
}

interface UpNextBadgeProps {
  isDragging?: boolean;
}

export function UpNextBadge({ isDragging }: UpNextBadgeProps) {
  const badgeStyle = isDragging ? {} : {
    boxShadow: '0 2px 8px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
  };

  const badgeClassName = isDragging 
    ? "bg-blue-100 text-blue-800 border border-blue-200 shadow-sm" 
    : "bg-blue-100/60 backdrop-blur-sm text-blue-800/90 border border-blue-200/50";

  return (
    <Badge className={`ml-2 ${badgeClassName}`} style={badgeStyle}>
      <div className="w-2 h-2 bg-blue-500 rounded-full mr-1 animate-pulse drop-shadow-sm"></div>
      Up Next
    </Badge>
  );
}